import type {ReactElement} from 'react';
import {useContext} from 'react';
import React from 'react';
import {DisplayType, SelectionType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PanelHeader} from '@Components/panel';
import {IconSize} from '@Components/icon-v2/icon.types';
import {handleBackButtonClick} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {useOrderDetails, useSelectionType, useShowLoader} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import type {PublishingPanelHeaderProps} from './publishing-panel-header.types';
import styles from './publishing-panel-header.module.scss';
import {useAppDispatch} from '@/hooks';
import {ModalDetails} from '@Components/modal/modal-context';

export function PublishingPanelHeader({displayType, onClose, showShadow}: PublishingPanelHeaderProps): ReactElement<any> | null {
  const dispatch = useAppDispatch();
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const onCustomClose = onClose;
  const handleBackButton = (): void => {
    dispatch(handleBackButtonClick());
  };

  const orderDetails = useOrderDetails();
  const currentSelectionType = useSelectionType();
  const showLoader = useShowLoader();
  const getLoaderText = (): string => {
    if (orderDetails.error !== '') {
      return window.i18next.t('pmwjs_error');
    }
    return window.i18next.t('pmwjs_prep');
  };

  const getHeaderForDropDown = (): ReactElement<any> | null => {
    const shadowClass = showShadow ? 'shadow-static-elevation-3 hover-transition-all' : '';
    switch (currentSelectionType) {
      case SelectionType.DEFAULT_DISPLAY:
        return null;
      case SelectionType.DIGITAL_SIGNAGE_DISPLAY:
        return (
          <PanelHeader
            title={showLoader ? getLoaderText() : window.i18next.t('pmwjs_digital_signage')}
            showBackButton={!showLoader}
            onBackButton={handleBackButton}
            showCloseButton={false}
            className={`_no-border-bottom ${shadowClass} ${styles.settingPanelHeightToAuto} spacing-p-3`}
            backButtonIconSize={IconSize.SIZE_ICON_16}
            forceCenterAlignTitleWithActionItems={showLoader}
            titleClasses="body-md-bold"
          />
        );

      case SelectionType.PUBLISH_WEBPAGE_DISPLAY:
        return (
          <PanelHeader
            title={window.i18next.t('pmwjs_publish_weblink')}
            showBackButton
            onBackButton={handleBackButton}
            showCloseButton={false}
            onClose={onCustomClose}
            backButtonIconSize={IconSize.SIZE_ICON_16}
            className={`_no-border-bottom ${styles.settingPanelHeightToAuto}`}
            titleClasses="body-md-bold"
          />
        );
      case SelectionType.EMBED_WEBPAGE_DISPLAY:
        return (
          <PanelHeader
            title={showLoader ? getLoaderText() : window.i18next.t('pmwjs_embed_in_webpage')}
            showBackButton={!showLoader}
            onBackButton={handleBackButton}
            showCloseButton={false}
            onClose={onCustomClose}
            backButtonIconSize={IconSize.SIZE_ICON_16}
            className={`_no-border-bottom ${styles.settingPanelHeightToAuto}`}
            forceCenterAlignTitleWithActionItems={showLoader}
            titleClasses="body-md-bold spacing-m-0"
          />
        );

      case SelectionType.SHARE_TO_APP:
        return (
          <PanelHeader
            title={showLoader ? getLoaderText() : window.i18next.t('pmwjs_share_to_app')}
            showBackButton={!showLoader}
            onBackButton={handleBackButton}
            showCloseButton={false}
            backButtonIconSize={IconSize.SIZE_ICON_16}
            className={`_no-border-bottom ${styles.settingPanelHeightToAuto}`}
            forceCenterAlignTitleWithActionItems={showLoader}
            titleClasses="body-md-bold spacing-m-0"
          />
        );

      case SelectionType.SOCIAL_MEDIA_LINK:
        return (
          <PanelHeader
            title={window.i18next.t('pmwjs_share_link_social')}
            showBackButton
            onBackButton={handleBackButton}
            showCloseButton={false}
            backButtonIconSize={IconSize.SIZE_ICON_16}
            className={`_no-border-bottom ${styles.settingPanelHeightToAuto} spacing-p-3`}
            titleClasses="body-md-bold"
          />
        );

      case SelectionType.QR_CODE:
        return (
          <PanelHeader
            title={window.i18next.t('pmwjs_qrcode')}
            showBackButton
            onBackButton={handleBackButton}
            showCloseButton={false}
            backButtonIconSize={IconSize.SIZE_ICON_16}
            className={`_no-border-bottom ${styles.settingPanelHeightToAuto} spacing-p-3`}
            titleClasses="body-md-bold"
          />
        );
      default:
        return null;
    }
  };

  const getHeaderForPanel = (): ReactElement<any> | null => {
    const shadowClass = showShadow ? 'shadow-static-elevation-3 hover-transition-all' : '';
    switch (currentSelectionType) {
      case SelectionType.DEFAULT_DISPLAY:
        return (
          <PanelHeader
            onClose={onCustomClose}
            showBackButton={false}
            centerAlignTitle={inMobile}
            isOpenedInModal
            className={shadowClass}
            title={window.i18next.t('pmwjs_publish_your_design')}
          />
        );
      case SelectionType.DIGITAL_SIGNAGE_DISPLAY:
        return (
          <PanelHeader
            onClose={onCustomClose}
            title={showLoader ? getLoaderText() : window.i18next.t('pmwjs_digital_signage')}
            showBackButton={!showLoader}
            onBackButton={handleBackButton}
            showCloseButton
            className={shadowClass}
          />
        );
      case SelectionType.PUBLISH_WEBPAGE_DISPLAY:
        return (
          <PanelHeader
            onClose={onCustomClose}
            title={window.i18next.t('pmwjs_publish_weblink')}
            showBackButton
            onBackButton={handleBackButton}
            showCloseButton
            centerAlignTitle={inMobile}
            isOpenedInModal
          />
        );
      case SelectionType.QR_CODE:
        return (
          <PanelHeader
            onClose={onCustomClose}
            title={window.i18next.t('pmwjs_qrcode')}
            showBackButton
            onBackButton={handleBackButton}
            showCloseButton
            centerAlignTitle={inMobile}
            isOpenedInModal
          />
        );
      case SelectionType.EMBED_WEBPAGE_DISPLAY:
        return (
          <PanelHeader
            onClose={onCustomClose}
            title={showLoader ? getLoaderText() : window.i18next.t('pmwjs_embed_in_webpage')}
            showBackButton={!showLoader}
            onBackButton={handleBackButton}
            showCloseButton
          />
        );
      case SelectionType.SHARE_TO_APP:
        return (
          <PanelHeader
            onClose={onCustomClose}
            title={showLoader ? getLoaderText() : window.i18next.t('pmwjs_share_to_app')}
            showBackButton={!showLoader}
            onBackButton={handleBackButton}
            showCloseButton
          />
        );
      case SelectionType.SOCIAL_MEDIA_LINK:
        return <PanelHeader onClose={onCustomClose} title={window.i18next.t('pmwjs_share_link_social')} showBackButton onBackButton={handleBackButton} showCloseButton />;
      default:
        return null;
    }
  };

  return displayType === DisplayType.DROPDOWN ? getHeaderForDropDown() : getHeaderForPanel();
}
